import { useState, useEffect, useContext } from "react";
import { Graph } from "../../../generated/graph";
import { gql, useMutation, useSubscription } from "@apollo/client";
import { useLazyQuery } from "@apollo/client";
import { ContextUser } from "src/context/ContextUser";
import { ChatMessage } from "src/graphql/codegen_graphql";

export interface IConversation extends ChatMessage {
  file?: any;
  type?: Graph.AttachmentType;
  break?: boolean;
  lastChild?: boolean;
}
export interface IChannelInfo {
  color?: string;
  letter?: string;
  name?: string;
  image?: string;
}

const LIMIT = 50;

const QUERY_CHAT_CHANNEL_SEEN_LIST = gql`
  query ChatChannelSeenList($channel: String!) {
    chatChannelSeenList(channel: $channel) {
      entity {
        id
        name
        image
      }
      lastSeenTimestamp
    }
  }
`;

const QUERY_CHAT_CHANNEL = gql`
  query ChatChannel($channel: String!, $before: Int, $limit: Int) {
    chatChannel(channel: $channel, before: $before, limit: $limit) {
      id
      channel
      createdAt
      sender {
        id
        name
        image
        internalDisplayName
      }
      onlineStatus
      replyTo {
        id
        channel
        sender {
          id
          entity
          name
          internalDisplayName
          image
        }
        message {
          text
          packageId
          productId
          product {
            id
            code
            price
            title
            thumbnail
            discount
          }
          attachments {
            type
            src
            lat
            lng
            orderId
          }
          uuid
        }
        createdAt
        onlineStatus
      }
      message {
        text
        attachments {
          type
          src
          lat
          lng
          orderId
        }
        packageId
        productId
        product {
          id
          code
          price
          title
          thumbnail
          discount
        }
      }
    }
  }
`;

const MUTATION_MARK_CHANNEL_SEEN = gql`
  mutation MarkChannelSeen($channel: String!) {
    markChannelSeen(channel: $channel)
  }
`;

const SUBSCRIPTION_CHAT_MESSAGE = gql`
  subscription {
    chatMessageAdded {
      id
      channel
      sender {
        id
        name
        entity
      }
      message {
        text
        attachments {
          type
          src
        }
      }
      createdAt
    }
  }
`;

export default (channel: string) => {
  const { merchant } = useContext(ContextUser);
  const ownerId = Number(merchant?.id);

  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadMore, setLoadMore] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const [list, setList] = useState<IConversation[]>([]);
  const [seens, setSeens] = useState<Graph.ChatChannelSeen[]>([]);
  const [seenId, setSeenId] = useState<number>(0);

  const [onMarkChannelSeen] = useMutation<{ markChannelSeen: boolean }>(MUTATION_MARK_CHANNEL_SEEN, {
    variables: { channel },
    refetchQueries: ["ChatChannelList"],
  });

  const [getChatChannel, { data: chats, loading: refreshing }] = useLazyQuery<{ chatChannel: ChatMessage[] }>(
    QUERY_CHAT_CHANNEL,
    {
      variables: { channel, before: 0 },
      onCompleted: (r) => {
        if (!!r.chatChannel) {
          const conversations = r.chatChannel || [];
          if (list.length === 0 || offset === 0) {
            setList(conversations);
          } else {
            setList((prev: ChatMessage[]) => prev.concat(conversations));
          }
          const last = conversations.slice(-1)[0];
          if (last) {
            setOffset(Number(last.id));
          }
          if (loadMore) {
            setLoadMore(false);
          }
          loading && setLoading(false);
          setHasMore(conversations.length === LIMIT);
        }
      },
      onError: (e) => {
        loading && setLoading(false);
      },
    },
  );

  const [getChatChannelSeenList] = useLazyQuery<{
    chatChannelSeenList?: Graph.ChatChannelSeen[];
  }>(QUERY_CHAT_CHANNEL_SEEN_LIST, {
    variables: { channel },
    onCompleted: (r) => {
      setSeens(r.chatChannelSeenList || []);
    },
  });

  const { data: newChatMessage } = useSubscription<Graph.Subscription>(SUBSCRIPTION_CHAT_MESSAGE);

  useEffect(() => {
    setList([]);
    setOffset(0);
    onMarkChannelSeen({ variables: { channel } });
    getChatChannelSeenList({ variables: { channel } });
    getChatChannel({ variables: { channel, before: 0 } });
  }, [channel, onMarkChannelSeen, getChatChannel, getChatChannelSeenList]);

  useEffect(() => {
    if (loading && !refreshing && chats?.chatChannel) {
      setLoading(false);
    }
    if (list.length > 0 && seens.length > 0) {
      const r = [...seens];
      r.sort((a, b) => Number(b.lastSeenTimestamp) - Number(a.lastSeenTimestamp));
      const seener = r.find((s) => s.entity?.id !== ownerId);
      const seenItem = list.find(
        (l) => !!l.id && !!l.createdAt && !!seener?.lastSeenTimestamp && l.createdAt <= seener.lastSeenTimestamp,
      );
      if (seenItem?.id && seenId < seenItem?.id) {
        setSeenId(seenItem?.id || 0);
      }
    }
  }, [
    list,
    seens,
    getChatChannel,
    getChatChannelSeenList,
    onMarkChannelSeen,
    chats,
    loading,
    ownerId,
    refreshing,
    seenId,
  ]);

  useEffect(() => {
    if (newChatMessage) {
      const added = newChatMessage.chatMessageAdded;
      console.log("----------New chat-------------");
      if (added?.channel === channel) {
        setList([]);
        setOffset(0);
        onMarkChannelSeen({ variables: { channel } });
        getChatChannelSeenList({ variables: { channel } });
        getChatChannel({ variables: { channel, before: 0 } });
      }
      if (added?.sender?.id !== ownerId) {
        onMarkChannelSeen({ variables: { channel } });
      }
    }
  }, [newChatMessage, channel, getChatChannel, getChatChannelSeenList, onMarkChannelSeen, ownerId]);

  const onEndReached = () => {
    if (!loadMore && hasMore && !loading && !refreshing) {
      setLoadMore(true);
      getChatChannel({ variables: { before: offset } });
    }
  };

  const onUpdateSeener = (senner: Graph.ChatChannelSeen) => {
    const r = [...seens];
    const index = r.findIndex((f) => f.entity?.id === senner.entity?.id);
    if (index > -1) {
      r[index] = senner;
      setSeens(r);
    }
  };

  return {
    seenId,
    offset,
    loading,
    refreshing,
    hasMore,
    list,
    ownerId,
    setList,
    setSeenId,
    setOffset,
    onEndReached,
    onUpdateSeener,
    onMarkChannelSeen,
  };
};
